// src/pages/TherapistPage/CaseHistory/ClinicalPanel.js
import React, { useCallback, useEffect, useState } from 'react';
import {
    getFormSchema, getSignals, getTriage, listImpressions, overrideHearing, saveImpression,
} from './caseFileApi';

const TIMEPOINT_ORDER = ['at_intake', 'at_capture', 'post_assessment'];

// These are answers about the whole picture, not conditions that can contribute
// to another one: nothing is "mainly dysarthria with within-normal-limits
// features".
const NOT_A_FEATURE = ['mixed', 'none', 'undetermined'];

/** Signals, hypothesis and the three timepoints side by side. */
export default function ClinicalPanel({ caseId }) {
    const [catalogue, setCatalogue] = useState(null);
    const [triage, setTriage] = useState(null);
    const [history, setHistory] = useState([]);
    const [timepoint, setTimepoint] = useState('at_intake');
    const [signals, setSignals] = useState({});
    const [hypothesis, setHypothesis] = useState('');
    const [secondary, setSecondary] = useState([]);
    const [severity, setSeverity] = useState('');
    const [rationale, setRationale] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    // Which earlier timepoint the form on screen was filled from, so the
    // therapist is told rather than left wondering where the ticks came from.
    const [carriedFrom, setCarriedFrom] = useState(null);
    // The hearing override, asked properly instead of through window.prompt —
    // a one-word answer to a one-line prompt is how "yes" ended up in a record.
    const [overriding, setOverriding] = useState(false);
    // Which obstacle, and the detail. Free text alone let "this child has no
    // hearing issue" through — a hearing conclusion with no test behind it, which
    // is the one thing this gate exists to stop. The obstacles come from the
    // content file and none of them is a judgement about the child's hearing.
    const [obstacle, setObstacle] = useState('');
    const [overrideDetail, setOverrideDetail] = useState('');
    const [obstacles, setObstacles] = useState([]);

    const load = useCallback(() => {
        Promise.all([getSignals(), getTriage(caseId), listImpressions(caseId)])
            .then(([cat, tri, hist]) => {
                setCatalogue(cat.data); setTriage(tri.data); setHistory(hist.data); setError('');
            })
            .catch(() => setError('Could not load the clinical view.'));
        // Separately, and without failing the tab: the obstacle list is only
        // needed if the gate turns out to be shut.
        getFormSchema()
            .then((res) => setObstacles(res.data?.hearing_override_reasons || []))
            .catch(() => setObstacles([]));
    }, [caseId]);

    useEffect(() => { load(); }, [load]);

    // Start from this timepoint's own impression, so a therapist edits forward
    // rather than retyping. When it has none yet, carry the most recent earlier
    // reading forward instead of blanking the form: a final impression normally
    // begins as the working hypothesis, and silently clearing the screen is how
    // the first real sign-off ended up freezing an empty conclusion.
    useEffect(() => {
        if (!triage || !catalogue) return;
        const order = catalogue.timepoints.map((t) => t.key);
        const own = triage.latest?.[timepoint];
        let source = own;
        let from = null;
        if (!own) {
            for (let i = order.indexOf(timepoint) - 1; i >= 0; i -= 1) {
                if (triage.latest?.[order[i]]) {
                    source = triage.latest[order[i]];
                    from = catalogue.timepoints.find((t) => t.key === order[i]);
                    break;
                }
            }
        }
        setSignals(source?.signals || {});
        setHypothesis(source?.hypothesis || '');
        setSecondary(source?.secondary_hypotheses || []);
        setSeverity(source?.severity || '');
        setRationale('');
        setCarriedFrom(from);
    }, [triage, catalogue, timepoint]);

    if (error) return <div className="cf-note red">{error}</div>;
    if (!catalogue || !triage) return <div className="cf-empty">Loading…</div>;

    const locked = !triage.hearing.satisfied;

    // Why a control is dead, in the control's own tooltip. Greying something out
    // says "no" without saying why, and the two reasons here are not alike: the
    // gate is one decision for the whole tab, while a signal with no recording
    // behind it is waiting on a different person entirely.
    const LOCK_REASON = 'Read-only: no hearing screen is on record for this child yet.';
    const needsEvidence = (signal) =>
        `Not assessable yet — needs ${catalogue.evidence_blocks[signal.evidence].en}.`;
    const deadBecause = (signal) => {
        if (locked) return LOCK_REASON;
        if (signal && !triage.availability[signal.key]) return needsEvidence(signal);
        return undefined;
    };
    /** title + aria-disabled for a control that is off, and nothing when it is live. */
    const why = (signal) => {
        const reason = deadBecause(signal);
        return reason ? { title: reason, 'aria-disabled': true } : {};
    };

    const setSignal = (key, value) => setSignals((prev) => {
        const next = { ...prev };
        if (next[key] === value) delete next[key];    // tapping the same answer unsets it
        else next[key] = value;
        return next;
    });

    const counts = (map) => {
        const out = {};
        catalogue.families.forEach((f) => {
            out[f.key] = {
                present: f.signals.filter((s) => map[s.key] === 'present').length,
                total: f.signals.length,
            };
        });
        return out;
    };

    const closeOverride = () => {
        setOverriding(false); setObstacle(''); setOverrideDetail('');
    };

    const doOverride = async () => {
        setBusy(true); setError('');
        try {
            await overrideHearing(caseId, obstacle, overrideDetail.trim() || null);
            setMessage('Recorded. The obstacle and your note are now part of this case.');
            closeOverride();
            load();
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not record the override.');
        } finally { setBusy(false); }
    };

    // "Another obstacle" says nothing on its own. The server applies the same
    // rule; this only saves the therapist a round trip.
    const overrideUsable = !!obstacle
        && (obstacle !== 'other' || overrideDetail.trim().length >= 10);

    // An impression with nothing in it is not a reading of the case, and the
    // server refuses it. `undetermined` exists for a therapist who cannot say
    // yet — that is an answer, and this is not.
    const somethingSaid = !!hypothesis || Object.keys(signals).length > 0 || !!rationale.trim();
    const finalTimepoint = timepoint === 'post_assessment';

    const save = async () => {
        setBusy(true); setError('');
        try {
            await saveImpression(caseId, { timepoint, signals, hypothesis: hypothesis || null,
                secondary_hypotheses: secondary.length ? secondary : null,
                severity: severity || null, rationale: rationale || null });
            setMessage('Impression recorded. Earlier readings are kept.');
            load();
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Could not save the impression.');
        } finally { setBusy(false); }
    };

    return (
        <div>
            {message && <div className="cf-note green">{message}</div>}

            {locked && (
                <div className="cf-note red">
                    <b>This tab is read-only until a hearing screen is on record.</b>
                    <div style={{ marginTop: 6 }}>{triage.hearing.blocked_reason}</div>
                    <div className="cf-hint" style={{ marginTop: 6 }}>
                        Untreated hearing loss looks exactly like a speech sound disorder from the
                        outside — the same sounds go missing. Scoring one as the other is the
                        mistake this gate exists to prevent, which is why it asks for a reason
                        rather than simply letting the tab open.
                    </div>
                    {overriding ? (
                        <div className="cf-field" style={{ marginTop: 10 }}>
                            <label>Which obstacle applies?</label>
                            <div className="cf-obstacles">
                                {obstacles.map((item) => (
                                    <label key={item.key} className="cf-obstacle">
                                        <input
                                            type="radio" name="cf-obstacle" value={item.key}
                                            checked={obstacle === item.key}
                                            onChange={() => setObstacle(item.key)}
                                        />
                                        <span>
                                            {item.en}
                                            {item.hi ? <em className="hi">{item.hi}</em> : null}
                                        </span>
                                    </label>
                                ))}
                            </div>
                            {obstacles.length === 0 && (
                                <p className="cf-hint">The obstacle list could not be loaded.</p>
                            )}

                            <label htmlFor="cf-override" style={{ marginTop: 8 }}>
                                Details{obstacle === 'other' ? '' : ' (optional)'}
                            </label>
                            <textarea
                                id="cf-override" rows="3" value={overrideDetail}
                                onChange={(e) => setOverrideDetail(e.target.value)}
                                placeholder="Nearest centre is 90km; the family has no transport this month."
                            />
                            <p className="cf-hint">
                                The obstacle and this note are frozen into the record at sign-off,
                                and they are what a reader of this case sees in two years. Note
                                that none of the choices is a judgement that the child hears
                                normally — that cannot be reached without a screen, which is what
                                this gate is about.
                            </p>
                            <div className="cf-actions">
                                <button
                                    className="btn btn-primary"
                                    disabled={busy || !overrideUsable}
                                    onClick={doOverride}
                                >
                                    {busy ? 'Recording…' : 'Record this and open scoring'}
                                </button>
                                <button
                                    className="btn btn-secondary" disabled={busy}
                                    onClick={closeOverride}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="cf-actions">
                            <button className="btn btn-secondary" disabled={busy} onClick={() => setOverriding(true)}>
                                Proceed anyway, with a reason…
                            </button>
                        </div>
                    )}
                </div>
            )}
            {triage.hearing.overridden && (
                <div className="cf-note amber">
                    <b>No hearing screen.</b> {triage.hearing.override_reason}
                </div>
            )}

            <div className="cf-bar">
                <label className="cf-hint">Recording an impression for</label>
                <select value={timepoint} onChange={(e) => setTimepoint(e.target.value)}
                        disabled={locked} {...why()}>
                    {catalogue.timepoints.map((t) => <option key={t.key} value={t.key}>{t.en}</option>)}
                </select>
                <span className="cf-hint">{triage.history_count} recorded so far · nothing is ever overwritten</span>
            </div>

            {carriedFrom && (
                <div className="cf-note blue">
                    <b>Carried forward from “{carriedFrom.en}”.</b> This timepoint has no impression
                    of its own yet, so your earlier reading is on screen to edit. Nothing is saved
                    until you press save, and the earlier record stays as it is.
                </div>
            )}

            <div className="cf-families">
                {catalogue.families.map((family) => {
                    const c = counts(signals)[family.key];
                    // A therapist looking at a column of dead buttons needs to know the
                    // page is not broken — it is waiting on recordings nobody has made.
                    const waiting = family.signals.filter((sg) => !triage.availability[sg.key]).length;
                    return (
                        <div className="cf-family" key={family.key}>
                            <h4>
                                <span>{family.title.en}</span>
                                <span className="cf-count">{c.present} / {c.total}</span>
                            </h4>
                            <div className="desc">{family.description.en}</div>
                            {waiting > 0 && (
                                <div className="cf-why" style={{ marginBottom: 6 }}>
                                    {waiting} of {family.signals.length} need recordings first.
                                </div>
                            )}
                            {family.signals.map((signal) => {
                                const can = triage.availability[signal.key];
                                return (
                                    <div className={`cf-signal ${can ? '' : 'unavailable'}`} key={signal.key}>
                                        <span className="cf-tri">
                                            <button
                                                type="button"
                                                className={signals[signal.key] === 'present' ? 'yes' : ''}
                                                disabled={!can || locked}
                                                onClick={() => setSignal(signal.key, 'present')}
                                                {...why(signal)}
                                            >yes</button>
                                            <button
                                                type="button"
                                                className={signals[signal.key] === 'absent' ? 'no' : ''}
                                                disabled={!can || locked}
                                                onClick={() => setSignal(signal.key, 'absent')}
                                                {...why(signal)}
                                            >no</button>
                                        </span>
                                        <span>
                                            {signal.label.en}
                                            {!can && (
                                                <span className="cf-why">
                                                    unavailable — needs {catalogue.evidence_blocks[signal.evidence].en}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>

            <div className="cf-grid2">
                <div className="cf-field">
                    <label>Primary hypothesis</label>
                    <select value={hypothesis} onChange={(e) => setHypothesis(e.target.value)}
                            disabled={locked} {...why()}>
                        <option value="">Not stated</option>
                        {catalogue.hypotheses.map((h) => <option key={h.key} value={h.key}>{h.en}</option>)}
                    </select>
                </div>
                <div className="cf-field">
                    <label>Severity</label>
                    <select value={severity} onChange={(e) => setSeverity(e.target.value)}
                            disabled={locked} {...why()}>
                        <option value="">Not stated</option>
                        {catalogue.severities.map((s) => <option key={s.key} value={s.key}>{s.en}</option>)}
                    </select>
                </div>
            </div>

            {/* Contributing features name what a mixed presentation is mixed of.
                "Mixed" on its own destroyed the information: two children with
                opposite presentations both read as mixed, and nothing said which
                two conditions were involved. Only offered once a primary is
                named, because features contribute *to* something. */}
            <div className="cf-field">
                <label>Contributing features</label>
                {hypothesis ? (
                    <>
                        <div className="cf-labels">
                            {catalogue.hypotheses
                                .filter((h) => h.key !== hypothesis && !NOT_A_FEATURE.includes(h.key))
                                .map((h) => (
                                    <button
                                        key={h.key} type="button" disabled={locked} {...why()}
                                        className={`cf-label-btn${secondary.includes(h.key) ? ' on' : ''}`}
                                        onClick={() => setSecondary((prev) => (
                                            prev.includes(h.key)
                                                ? prev.filter((k) => k !== h.key)
                                                : [...prev, h.key]
                                        ))}
                                    >
                                        {h.en}
                                    </button>
                                ))}
                        </div>
                        <span className="cf-hint">
                            Optional. Reads as “mainly {catalogue.hypotheses.find((h) => h.key === hypothesis)?.en}
                            {secondary.length ? `, with ${secondary.length} contributing feature(s)` : ''}”.
                        </span>
                    </>
                ) : (
                    <span className="cf-hint">Name a primary hypothesis first.</span>
                )}
            </div>
            <div className="cf-field">
                <label>Rationale</label>
                <textarea value={rationale} onChange={(e) => setRationale(e.target.value)} disabled={locked} {...why()}
                          placeholder="What the signals above add up to, and what you are still waiting on." />
            </div>
            <div className="cf-bar" style={{ marginTop: 12 }}>
                <button
                    className="btn btn-primary"
                    disabled={locked || busy || !somethingSaid}
                    onClick={save}
                    title={locked ? LOCK_REASON
                        : somethingSaid ? undefined
                            : 'Nothing is marked yet — tick a signal, or name a hypothesis.'}
                    aria-disabled={locked || !somethingSaid}
                >
                    {busy ? 'Saving…' : 'Save impression'}
                </button>
                <span className="cf-hint">Saving adds a record — it never edits an earlier one.</span>
            </div>
            {!somethingSaid && !locked && (
                <p className="cf-hint">
                    Nothing is marked yet. Tick what you saw, or name a hypothesis —
                    <b> undetermined</b> is a hypothesis too.
                </p>
            )}
            {somethingSaid && !hypothesis && finalTimepoint && (
                <div className="cf-note amber">
                    <b>Sign-off will refuse this.</b> A final impression has to name a hypothesis —
                    it is the conclusion the frozen record keeps. If you cannot say yet, choose
                    <b> undetermined</b> and say why in the rationale.
                </div>
            )}

            <h3 style={{ margin: '22px 0 8px', fontSize: '1rem' }}>How the picture has changed</h3>
            <table className="cf-timeline">
                <thead>
                    <tr>
                        <th></th>
                        {TIMEPOINT_ORDER.map((t) => (
                            <th key={t}>{catalogue.timepoints.find((x) => x.key === t).en}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {catalogue.families.map((family) => {
                        const cells = TIMEPOINT_ORDER.map((t) => triage.latest[t]?.counts?.[family.key]);
                        const values = cells.filter(Boolean).map((c) => c.present);
                        const moved = values.length > 1 && new Set(values).size > 1;
                        return (
                            <tr key={family.key} className={moved ? 'moved' : ''}>
                                <td><b>{family.title.en}</b></td>
                                {cells.map((c, i) => (
                                    <td className="num" key={i}>
                                        {c ? `${c.present} / ${c.total}` : <span className="cf-hint">—</span>}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                    <tr>
                        <td><b>Hypothesis</b></td>
                        {TIMEPOINT_ORDER.map((t) => {
                            const key = triage.latest[t]?.hypothesis;
                            const label = catalogue.hypotheses.find((h) => h.key === key);
                            return <td key={t}>{label ? label.en : <span className="cf-hint">—</span>}</td>;
                        })}
                    </tr>
                    <tr>
                        <td><b>Severity</b></td>
                        {TIMEPOINT_ORDER.map((t) => {
                            const key = triage.latest[t]?.severity;
                            const label = catalogue.severities.find((s) => s.key === key);
                            return <td key={t}>{label ? label.en : <span className="cf-hint">—</span>}</td>;
                        })}
                    </tr>
                </tbody>
            </table>
            {history.length > 1 && (
                <div className="cf-hint" style={{ marginTop: 8 }}>
                    {history.length} readings recorded. The columns show the latest at each point;
                    earlier ones are kept and never changed.
                </div>
            )}
        </div>
    );
}
